import INTOUCH_API from "../../services/Api";
import { useContext, useEffect, useRef } from "react";
import { MODAL_TYPES } from "../../utils/emitter";
import { useLocation, useNavigate } from "react-router-dom";
import useModalActions from "../../hooks/useModalActions";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import {
  INSIGHT_FORM_STATUSES,
  NOTIFY_MODAL_TYPES,
} from "../../utils/constants";
import useCurrentGame from "../../hooks/useCurrentGame";
import { SOCKET_EVENTS, SocketContext } from "../../components/SocketProvider";
import ROUTES from "../../utils/routes.js";

export default function useController({
  questions,
  isFormValid,
  hasChangesForm,
  onSetHasChanges,
  questionInfo,
  blankTitle,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { sendEvent } = useContext(SocketContext);
  const { openModal } = useModalActions();
  const { getCurrentGame } = useCurrentGame();
  const { getUserData } = useCurrentUserData();

  const sendFormAnswer = () => {
    if (!isFormValid || !location.state.formId) return;
    const game = getCurrentGame();
    const form = {
      partnerId: game.partner.id,
      questionId: questionInfo.questionId,
      gameId: game.id,
      answers: questions.map(({ value, number }) => ({
        value,
        numberQuestion: number,
      })),
      // BLANK: тема редактируется в форме (Figma 11691-46635) — шлём актуальное
      // название, бэк обновит его у обеих анкет пары. items 17/20b: название
      // задаёт ТОЛЬКО автор анкеты (isCreated); получатель название не правит,
      // поэтому customTitle от него не шлём (иначе перезатёр бы тему автора).
      ...(questionInfo.formType === "BLANK" &&
      questionInfo.isCreated &&
      blankTitle?.trim()
        ? { customTitle: blankTitle.trim() }
        : {}),
    };

    INTOUCH_API.requestManager(
      "sendInsightFormAnswer",
      {
        payload: form,
        formId: location.state.formId,
      },
      () => {
        if (questionInfo) {
          const game = getCurrentGame();
          const user = getUserData();

          const typeNotifyModal =
            questionInfo.status === INSIGHT_FORM_STATUSES.REJECTED ||
            !questionInfo.isCreated
              ? NOTIFY_MODAL_TYPES.NOTIFY_PARTNER
              : NOTIFY_MODAL_TYPES.INVITE_PARTNER;

          const eventData = {
            room: game.id,
            // у BLANK-анкеты нет карточки — пара ищется по pairKey
            questionId: questionInfo.question?.id ?? null,
            pairKey: questionInfo.pairKey ?? null,
            message: "FuCK",
            recipientId: game.partner.id,
            senderId: user.id,
            typeNotifyModal,
            status: INSIGHT_FORM_STATUSES.WANT_TO_IMPROVE,
          };

          console.log("📤 Отправляем insight событие:", eventData);
          sendEvent(SOCKET_EVENTS.SEND_INSIGHT_MESSAGE, eventData);

          if (
            questionInfo.status === INSIGHT_FORM_STATUSES.REJECTED ||
            !questionInfo.isCreated
          ) {
            openModal(MODAL_TYPES.INSIGHT_ANSWER_PARTNER_SENT);
          } else {
            openModal(MODAL_TYPES.INSIGHT_INVITE_PARTNER_SENT);
          }
          saveDraft();
        }
      }
    );
  };

  // item 4: черновик сохраняет и ТЕМУ BLANK-анкеты. Попап названия убран, тема вводится
  // первым блоком формы — без этого набранное название терялось при уходе со страницы.
  const buildDraftPayload = (override = {}) => {
    const qs = override.questions ?? questions;
    const payload = {
      formId: location.state.formId,
      questions: qs,
    };
    if (questionInfo?.formType === "BLANK") {
      payload.customTitle = override.blankTitle ?? blankTitle ?? "";
    }
    return payload;
  };

  // item 4: АВТОСОХРАНЕНИЕ черновика (клиент: «начала заполнять, вышла — должна
  // сохраниться автоматом»). Раньше сохранял только ручной «Сохранить черновик», а
  // модалка «Продолжить/Остаться» (LeaveSavingQuestionnaireForm) вообще нигде не
  // использовалась — уход со страницы терял ответы.
  // Дебаунс, а не сохранение только на анмаунте: на мобильных вкладку могут убить без
  // размонтирования, и запрос, начатый в момент выгрузки страницы, браузер отменяет.
  // Флеш на анмаунте оставляем как добор для быстрых уходов.
  // Своя отметка «что уже сохранено» вместо hasChangesForm: тот сравнивает только
  // ответы, а тема (blankTitle) — отдельное поле, и правка одной темы его не поднимала.
  const AUTOSAVE_DELAY_MS = 1500;
  const lastSavedRef = useRef(null);
  const flushRef = useRef(null);
  const latestRef = useRef({ questions, blankTitle });
  latestRef.current = { questions, blankTitle };

  const snapshotOf = (payload) =>
    JSON.stringify({
      q: (payload.questions || []).map((x) => ({ n: x.number, v: x.value })),
      t: payload.customTitle ?? null,
    });

  const saveDraft = (override = {}) => {
    if (!location.state?.formId) return;
    const payload = buildDraftPayload(override);
    INTOUCH_API.requestManager(
      "sendInsightFormDraft",
      payload,
      () => {
        lastSavedRef.current = snapshotOf(payload);
      },
      {},
      () => {
        onSetHasChanges(false);
      }
    );
  };

  useEffect(() => {
    INTOUCH_API.requestManager(
      "getInsightFormDraftCurrent",
      { payload: location.state.formId },
      (response) => {
        if (!response.data) {
          saveDraft();
        }

        if (
          response.data &&
          questions.length &&
          questions.some((question) => question.value.length) &&
          !hasChangesForm
        ) {
          questions.forEach((question) => {
            const forComparison = response.data?.questions.find(
              (compareItem) => question.number === compareItem.number
            );
            if (
              question?.value.length &&
              forComparison?.value !== question?.value
            ) {
              onSetHasChanges(true);
            }
          });
        }
      }
    );
  }, [questions, hasChangesForm, location.state.formId]);

  // дебаунс-автосохранение при изменениях
  useEffect(() => {
    if (!location.state?.formId) return undefined;
    const current = snapshotOf(buildDraftPayload());
    // первый проход: фиксируем загруженное состояние, ничего не отправляем
    if (lastSavedRef.current === null) {
      lastSavedRef.current = current;
      return undefined;
    }
    if (current === lastSavedRef.current) return undefined;
    const timer = setTimeout(() => saveDraft(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, blankTitle, location.state?.formId, questionInfo?.formType]);

  // Добор на анмаунте держим в ref: эффект с [] замыкается на ПЕРВЫЙ рендер, где
  // questionInfo ещё null — тогда для BLANK-анкеты в payload не попало бы customTitle
  // и название при быстром уходе терялось бы (ровно то, что чиним).
  flushRef.current = () => {
    if (!location.state?.formId) return;
    const payload = buildDraftPayload(latestRef.current);
    if (lastSavedRef.current !== null && snapshotOf(payload) !== lastSavedRef.current) {
      saveDraft(latestRef.current);
    }
  };

  useEffect(() => {
    return () => flushRef.current?.();
  }, []);

  const rejectQuestion = () => {
    if (!questionInfo) return;
    const user = getUserData();
    const game = getCurrentGame();
    sendEvent(SOCKET_EVENTS.SEND_INSIGHT_MESSAGE, {
      questionId: questionInfo.questionId,
      pairKey: questionInfo.pairKey ?? null,
      room: game.id,
      message: "FuCK",
      recipientId: game.partner.id,
      senderId: user.id,
      status: INSIGHT_FORM_STATUSES.REJECTED,
    });
    navigate(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE);
  };

  return {
    rejectQuestion,
    sendFormAnswer,
    saveDraft,
  };
}
